"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MuiLink from "@mui/material/Link";
import { IconClose, IconMenu } from "@/components/icons";
import { Link } from "@/i18n/navigation";
import { folioSx, fonts } from "@/theme/tokens";
import { formatFolio } from "@/lib/format";
import { LanguageSelect } from "./LanguageSelect";
import { StartCta } from "./StartCta";

const linkSx = {
  fontFamily: fonts.heading,
  fontSize: "1.35rem",
  fontWeight: 420,
  color: "text.primary",
  display: "flex",
  alignItems: "baseline",
  gap: 1.5,
} as const;

/** Mono folio index rendered before each drawer entry. */
function FolioIndex({ n }: { n: number }) {
  return (
    <Box component="span" aria-hidden sx={{ ...folioSx, minWidth: 22 }}>
      {formatFolio(n)}
    </Box>
  );
}

/**
 * Navigation for viewports below `md`, where the mega menus are hidden: a
 * hamburger trigger opening a right-hand drawer with the home-page section
 * anchors, the sub-pages, and the primary CTA. Only rendered by the Header,
 * which appears solely on the home page — so plain hash hrefs are safe.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false);
  // Root translator: labels are reused from several existing namespaces.
  const t = useTranslations();
  const close = () => setOpen(false);

  const anchors = [
    { href: "#services", label: t("header.nav.services") },
    { href: "#process", label: t("header.nav.process") },
    { href: "#deliverables", label: t("deliverables.title") },
    { href: "#pricing", label: t("header.nav.pricing") },
  ];
  const pages = [
    { href: "/work", label: t("header.nav.work") },
    { href: "/about", label: t("header.nav.about") },
    { href: "/faq", label: t("footerNav.resources.links.faq") },
    { href: "/press", label: t("footerNav.resources.links.press") },
  ];

  return (
    <Box sx={{ display: { xs: "block", lg: "none" } }}>
      <IconButton
        aria-label={t("header.menuOpen")}
        onClick={() => setOpen(true)}
      >
        <IconMenu />
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        slotProps={{ paper: { sx: { width: 300 } } }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1.5 }}>
          <IconButton aria-label={t("header.menuClose")} onClick={close}>
            <IconClose />
          </IconButton>
        </Box>
        <Box
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            px: 3,
            pb: 4,
          }}
        >
          {anchors.map(({ href, label }, index) => (
            <MuiLink
              key={href}
              href={href}
              underline="none"
              onClick={close}
              sx={linkSx}
            >
              <FolioIndex n={index + 1} />
              {label}
            </MuiLink>
          ))}
          <Divider />
          {pages.map(({ href, label }, index) => (
            <MuiLink
              key={href}
              component={Link}
              href={href}
              underline="none"
              onClick={close}
              sx={linkSx}
            >
              <FolioIndex n={anchors.length + index + 1} />
              {label}
            </MuiLink>
          ))}
          <Divider />
          <LanguageSelect fullWidth />
          <StartCta variant="contained" onClick={close} sx={{ mt: 1.5 }}>
            {t("header.cta")}
          </StartCta>
        </Box>
      </Drawer>
    </Box>
  );
}
